import { userErrorMessage } from '@wx-weiqi/contracts';
import { useEffect, useState } from 'react';
import {
  ReloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ActivityLogIcon,
} from '@radix-ui/react-icons';
import type { EbookStudyPage, EbookStudyQuery } from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import { EbookStudyReplayDialog } from './EbookStudyReplayDialog';
import { StudyResult, studyDuration, studyStatus } from './EbookStudyPresentation';
import './ebook-records-workspace.css';

export function EbookStudyRecords({
  client,
  documentId,
  published,
  onPublish,
}: {
  client: EbookClient;
  documentId: string;
  published: boolean;
  onPublish: () => void;
}) {
  const [data, setData] = useState<EbookStudyPage | null>(null);
  const [query, setQuery] = useState<EbookStudyQuery>({ page: 1 });
  const [student, setStudent] = useState('');
  const [question, setQuestion] = useState('');
  const [status, setStatus] = useState('');
  const [date, setDate] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attemptId, setAttemptId] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    void client
      .studyRecords(documentId, query)
      .then((result) => {
        if (active) setData(result);
      })
      .catch((e: unknown) => {
        if (active) setError(userErrorMessage(e, '学习记录加载失败，请重试'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [client, documentId, query, refresh]);
  const filtered = !!(query.studentId || query.questionId || query.status || query.from);
  const reset = () => {
    setStudent('');
    setQuestion('');
    setStatus('');
    setDate('');
    setQuery({ page: 1 });
  };
  return (
    <section
      className="ebook-study ebook-records-workspace"
      aria-label="学生学习记录"
      aria-busy={loading}
    >
      <header className="ebook-records-heading">
        <h2>查看每一次练习</h2>
        <p>按学生、题目和结果查找，打开记录可回放落子过程。</p>
      </header>
      <form
        className="ebook-study-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const from = date ? new Date(`${date}T00:00:00`) : null;
          const to = from ? new Date(from) : null;
          to?.setDate(to.getDate() + 1);
          setQuery({
            page: 1,
            studentId: student || undefined,
            questionId: question || undefined,
            status: (status || undefined) as EbookStudyQuery['status'],
            from: from?.toISOString(),
            to: to?.toISOString(),
          });
        }}
      >
        <label>
          学生
          <select value={student} onChange={(e) => setStudent(e.target.value)}>
            <option value="">全部学生</option>
            {data?.students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          题目
          <select value={question} onChange={(e) => setQuestion(e.target.value)}>
            <option value="">全部题目</option>
            {data?.questions.map((q) => (
              <option key={q.id} value={q.id}>
                {q.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          结果
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">全部结果</option>
            {Object.entries(studyStatus).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          作答日期
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <button className="primary" disabled={loading}>
          筛选
        </button>
        <button type="button" disabled={loading} onClick={reset}>
          清除筛选
        </button>
      </form>
      <div className="ebook-study-list-heading">
        <p aria-live="polite">
          {data ? (
            <>
              共 <strong>{data.total}</strong> 次作答{filtered ? ' · 已筛选' : ''}
            </>
          ) : (
            '正在读取学习记录'
          )}
        </p>
        <button disabled={loading} onClick={() => setRefresh((n) => n + 1)}>
          <ReloadIcon aria-hidden="true" />
          刷新
        </button>
      </div>
      {error ? (
        <div role="alert" className="ebook-error">
          {error}
          <button onClick={() => setRefresh((n) => n + 1)}>重新加载</button>
        </div>
      ) : loading && !data ? (
        <div className="ebook-study-skeleton" role="status" aria-label="正在加载学习记录">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} />
          ))}
        </div>
      ) : !data?.items.length ? (
        <div className="ebook-study-empty">
          <ActivityLogIcon aria-hidden="true" />
          <h3>{filtered ? '没有符合条件的作答' : '还没有学生作答'}</h3>
          <p>
            {filtered
              ? '换个学生、题目或日期试试。'
              : published
                ? '学生进入电子书练习后，每次作答会显示在这里。'
                : '先发布给班级，学生的练习记录会自动汇总到这里。'}
          </p>
          {filtered ? (
            <button onClick={reset}>清除筛选</button>
          ) : !published ? (
            <button className="primary" onClick={onPublish}>
              去发布电子书
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="ebook-study-table-wrap">
            <table className="ebook-study-table">
              <caption className="ebook-sr-only">每题每次作答，按开始时间从新到旧排列</caption>
              <thead>
                <tr>
                  <th scope="col">学生</th>
                  <th scope="col">题目</th>
                  <th scope="col">作答时间</th>
                  <th scope="col">用时</th>
                  <th scope="col">结果</th>
                  <th scope="col">
                    <span className="ebook-sr-only">查看</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} data-review={r.status === 'PENDING_REVIEW'}>
                    <td data-label="学生">
                      <strong>{r.studentName}</strong>
                    </td>
                    <td data-label="题目">
                      <span title={r.questionTitle}>{r.questionLabel}</span>
                      <span className="ebook-study-secondary">
                        第 {r.attemptNumber} 次 · {r.moveCount} 手
                      </span>
                    </td>
                    <td data-label="作答时间">
                      <time dateTime={r.startedAt}>
                        {new Date(r.startedAt).toLocaleDateString('zh-CN')}
                        <span className="ebook-study-secondary">
                          {new Date(r.startedAt).toLocaleTimeString('zh-CN', { hour12: false })}
                        </span>
                      </time>
                    </td>
                    <td data-label="用时">{studyDuration(r.durationMs)}</td>
                    <td data-label="结果">
                      <StudyResult status={r.status} />
                    </td>
                    <td>
                      <button
                        className="ebook-study-open"
                        disabled={loading}
                        onClick={() => setAttemptId(r.id)}
                        aria-label={`查看 ${r.studentName} ${r.questionLabel}第 ${r.attemptNumber} 次作答过程`}
                      >
                        {r.status === 'PENDING_REVIEW' ? '回放并批改' : '查看作答'}
                        <ChevronRightIcon aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="ebook-study-pagination">
            <span>每页 {data.pageSize} 条</span>
            <div>
              <button
                aria-label="上一页记录"
                disabled={loading || data.page <= 1}
                onClick={() => setQuery({ ...query, page: data.page - 1 })}
              >
                <ChevronLeftIcon aria-hidden="true" />
                上一页
              </button>
              <span>
                {data.page} / {Math.max(1, Math.ceil(data.total / data.pageSize))}
              </span>
              <button
                aria-label="下一页记录"
                disabled={loading || data.page * data.pageSize >= data.total}
                onClick={() => setQuery({ ...query, page: data.page + 1 })}
              >
                下一页
                <ChevronRightIcon aria-hidden="true" />
              </button>
            </div>
          </footer>
        </>
      )}
      <p className="ebook-study-footnote">
        记录包含历次发布的题目。回放保留作答时的棋形和答案；教师试做不计入。时间按当前设备时区显示。
      </p>
      {attemptId && (
        <EbookStudyReplayDialog
          client={client}
          documentId={documentId}
          attemptId={attemptId}
          onClose={() => setAttemptId(null)}
          onChanged={() => setRefresh((n) => n + 1)}
        />
      )}
    </section>
  );
}
